/* eslint-disable react-hooks/exhaustive-deps */
import React, { FC, PropsWithChildren, useEffect, useState } from 'react';
import EmptyPageLayout from '@/layouts/EmptyPage';
import { ILockIconProps } from '@/components/LockIcon';
import PasscodeButtons, {
  IPasscodeButtonsProps,
} from './components/PasscodeButtons';
import PasscodeFooterLink, {
  IPasscodeFooterLinkProps,
} from './components/PasscodeFooterLink';
import PasscodeHeader, {
  IPasscodeHeaderProps,
} from './components/PasscodeHeader';
import PasscodePreview, {
  IPasscodePreviewProps,
} from './components/PasscodePreview';
import { classnames } from '@/utils/classnames';
import { sleep } from '@/utils/date';
import { callWithMinTime } from '@/utils/function';
import { vibrate } from '@/utils/pwa';
import styles from '../../styles/animation.module.css';

export type TPasscodeVariant = ILockIconProps['variant'];

export interface IPasscodeKeypadProps extends PropsWithChildren<{}> {
  length?: number;
  previewTimeout?: IPasscodePreviewProps['timeout'];
  footerProps?: IPasscodeFooterLinkProps;
  title?: IPasscodeHeaderProps['title'];
  initialIsLocked?: IPasscodeHeaderProps['isLocked'];
  changeIsLockedIfSuccess?: boolean;

  validator: (__value: string) => Promisable<boolean>;
  onSuccess: (__value: string) => Promisable<void>;
  onSuccessDelayTimeout?: number;
  onError?: (__value: string, __error?: Error) => Promisable<void>;
  onErrorResetTimeout?: number;
  onAuthenticatorClick?: IPasscodeButtonsProps['onAuthenticatorClick'];
}

const PasscodeKeypad: FC<IPasscodeKeypadProps> = ({
  children,
  length = 4,
  previewTimeout = 0,
  footerProps = {},
  title,
  initialIsLocked = true,
  changeIsLockedIfSuccess = true,

  validator,
  onSuccess,
  onSuccessDelayTimeout = 1000,
  onError,
  onErrorResetTimeout = 1000,
  onAuthenticatorClick,
}) => {
  const [value, setValue] = useState<string>('');
  const [variant, setVariant] = useState<TPasscodeVariant>('default');
  const [isLocked, setIsLocked] = useState<boolean>(initialIsLocked);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleSuccess = async (value: string) => {
    setVariant('success');
    if (changeIsLockedIfSuccess) {
      setIsLocked((prevState) => !prevState);
    }
    if (onSuccessDelayTimeout) {
      await sleep(onSuccessDelayTimeout);
    }
    await onSuccess?.(value);
  };

  const handleError = async (value: string, error: any) => {
    setVariant('error');
    if (onErrorResetTimeout) {
      sleep(onErrorResetTimeout).then(() => setValue(''));
    }
    vibrate(100);
    await onError?.(value, error);
  };

  const handleValidate = async (value: string) => {
    setVariant('default');
    if (value?.length < length) return;

    try {
      setIsLoading(true);

      const isValid: boolean = await callWithMinTime(
        () => validator?.(value),
        previewTimeout
      );

      if (!isValid) await Promise.reject(null);
      await handleSuccess(value);
    } catch (error: any) {
      await handleError(value, error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    handleValidate(value).then();
  }, [value]);

  return (
    <EmptyPageLayout>
      <div className="flex w-full flex-1 select-none flex-col items-center justify-center gap-y-[33px] py-5 md:gap-y-[43px] md:py-10">
        <PasscodeHeader isLocked={isLocked} title={title} variant={variant} />

        <PasscodePreview
          length={length}
          timeout={previewTimeout}
          value={value}
          variant={variant}
        />

        <div className={classnames(styles['swing-in-bottom-fwd'])}>
          <PasscodeButtons
            isLoading={isLoading}
            length={length}
            value={value}
            onAuthenticatorClick={onAuthenticatorClick}
            onChange={setValue}
          />
        </div>

        {children}
        <PasscodeFooterLink {...footerProps} />
      </div>
    </EmptyPageLayout>
  );
};

export default PasscodeKeypad;
